import { Trash2 } from 'lucide-react';

import { Option } from '@@/form-components/PortainerSelect';
import { Button } from '@@/buttons';
import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';

import type {
  KubernetesRbacFormValues,
  KubernetesUnifiedAccess,
} from './types';
import { useColumns } from './useColumns';

const accessStorageKey = 'k8s-rbac-access-list';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface AccessListDatatableProps {
  formData: KubernetesRbacFormValues;
  onChange: (key: 'accessList', value: KubernetesUnifiedAccess[]) => void;
  roleOptions: Option<number>[];
}

export function AccessListDatatable({
  formData,
  onChange,
  roleOptions,
}: AccessListDatatableProps) {
  const accessList = formData.accessList || [];

  const tableState = useTableState(accessSettingsStore, accessStorageKey);

  return (
    <Datatable
      title="Access list"
      dataset={accessList}
      columns={useColumns({
        accessList,
        onChange,
        roleOptions,
      })}
      getRowId={(row) => `${row.Type}-${row.Id}`}
      settingsManager={tableState}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <TableActions
          selectedRows={selectedRows}
          onRemove={handleRemoveAccess}
        />
      )}
      data-cy="k8s-rbac-access-datatable"
      emptyContentLabel="No access configured. Add access for users, teams and/or registries above."
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function handleRemoveAccess(items: KubernetesUnifiedAccess[]) {
    const idsToRemove = new Set(items.map((item) => `${item.Type}-${item.Id}`));
    const newAccessList = accessList.filter(
      (access) => !idsToRemove.has(`${access.Type}-${access.Id}`)
    );
    onChange('accessList', newAccessList);
  }
}

function TableActions({
  selectedRows,
  onRemove,
}: {
  selectedRows: KubernetesUnifiedAccess[];
  onRemove: (items: KubernetesUnifiedAccess[]) => void;
}) {
  return (
    <Button
      color="dangerlight"
      size="small"
      icon={Trash2}
      onClick={() => onRemove(selectedRows)}
      data-cy="remove-access-button"
      disabled={selectedRows.length === 0}
    >
      Remove
    </Button>
  );
}
